import classNames from 'classnames'
import {
  setAnalysisItem,
  setAnalysisPeriod,
} from 'features/report/reportAddSlice'
import { useMemo, useState } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import {
  AD_ACCOUNT,
  AD_GROUP,
  CAMPAIGN,
  CREATIVE,
  getDomainText,
  KEYWORD,
} from 'features/report/common/DomainText'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import NoneAnchor from 'components/NoneAnchor'
import { CampaignTypes } from '../../Codes'
import useOutsideClick from '../../../utils/hooks/useOutsideClick'

const NONE = ['none', '없음']
const HOUR = ['hour', '시간대']
const MEDIA_TYPE = ['mediaType', '매체유형']
const TARGETING_TYPE = ['targetingType', '매칭유형']
const DEVICE = ['device', '디바이스']
const KEYWORD_EX = ['keywordEx', '키워드확장']
const ASSET_TYPE = ['assetType', '확장소재']
const TEMPLATE_CATEGORY = ['templateCategory', '소재타입']
const DAY = ['day', '일']
const WEEK = ['week', '주']
const MONTH = ['month', '월']

const AnalysisFilter = () => {
  const dispatch = useDispatch()
  const [visible, setVisible] = useState(false)
  const ref = useOutsideClick(() => setVisible(false))
  const {
    campaignType,
    domain,
    analysisItem,
    analysisPeriod,
    period: {
      relative,
      specific: { start: startDt, end: endDt },
    },
  } = useSelector(state => state.report.reportAdd.formValues)
  const analysisItemConfig = useMemo(() => {
    const days = moment().diff(moment(startDt), 'days')
    const config = []
    if (domain === AD_ACCOUNT) {
      config.push(NONE)
    } else if (domain === CAMPAIGN) {
      config.push(NONE, MEDIA_TYPE, TARGETING_TYPE, DEVICE)
    } else if (domain === AD_GROUP) {
      campaignType === CampaignTypes.TALK_CHANNEL
        ? config.push(NONE, MEDIA_TYPE, TARGETING_TYPE, DEVICE)
        : config.push(NONE, MEDIA_TYPE, DEVICE)
    } else if (domain === KEYWORD) {
      campaignType === CampaignTypes.TALK_CHANNEL
        ? config.push(NONE, DEVICE)
        : config.push(NONE, DEVICE, KEYWORD_EX)
    } else if (domain === CREATIVE) {
      campaignType === CampaignTypes.TALK_CHANNEL
        ? config.push(
            NONE,
            MEDIA_TYPE,
            TARGETING_TYPE,
            DEVICE,
            TEMPLATE_CATEGORY
          )
        : config.push(NONE, MEDIA_TYPE, DEVICE, ASSET_TYPE)
    }
    if (days <= 89 && relative !== 'all') {
      config.push(HOUR)
    }
    return config
  }, [campaignType, domain, relative, startDt])
  const analysisPeriodConfig = useMemo(() => {
    const period = moment(endDt).diff(moment(startDt), 'days')
    const periodInMonth = moment(endDt).diff(moment(startDt), 'months')
    if (periodInMonth > 12 || relative === 'all') {
      // [value, text, active]
      return [
        [...NONE, true],
        [...DAY, false],
        [...WEEK, false],
        [...MONTH, false],
      ]
    } else {
      if (period > 61) {
        return [
          [...NONE, true],
          [...DAY, false],
          [...WEEK, false],
          [...MONTH, true],
        ]
      } else if (period > 30) {
        return [
          [...NONE, true],
          [...DAY, false],
          [...WEEK, true],
          [...MONTH, true],
        ]
      } else {
        return [
          [...NONE, true],
          [...DAY, true],
          [...WEEK, true],
          [...MONTH, true],
        ]
      }
    }
  }, [startDt, endDt, relative])

  return (
    <div ref={ref} className="single_wrap">
      <div
        className={classNames('opt_select opt_report', {
          opt_open: visible,
        })}>
        <div className="screen_out">데이터종류 선택상자</div>
        <span className="screen_out">선택내용 : </span>
        <NoneAnchor
          className="link_selected"
          onClick={() => setVisible(prev => !prev)}>
          분석데이터
        </NoneAnchor>
        <span className="ico_arr" />
        <div className="screen_out">선택옵션</div>
        <div className="optlist_data">
          <div className="inner_optlist">
            <ul className="list_data">
              <li>
                <strong className="tit_data">
                  분석항목
                  <HelpTooltip>
                    &apos;시간대&apos; 항목은 조회 기간을 오늘을 포함한
                    <br />
                    최대 90일내로 선택한 경우에 제공 가능합니다.
                  </HelpTooltip>
                </strong>
                {analysisItemConfig.map(config => {
                  const [value, text] = config
                  return (
                    <NoneAnchor
                      key={`$$${value}`}
                      data-tiara-action-name={`보고서_${getDomainText(
                        domain
                      )}_분석데이터_분석항목_${text}_클릭`}
                      className={classNames('link_option', {
                        on: analysisItem === value,
                      })}
                      onClick={() => dispatch(setAnalysisItem(value))}>
                      {text}
                    </NoneAnchor>
                  )
                })}
              </li>
              <li>
                <strong className="tit_data">
                  분석기간단위
                  <HelpTooltip>
                    &apos;일&apos; 단위는 조회 기간을 1개월(31일) 내로,
                    <br />
                    &apos;주&apos; 단위는 조회 기간을 2개월(62일) 내로,
                    <br />
                    &apos;월&apos; 단위는 조회 기간을 12개월 내로 선택한 경우에
                    제공 가능합니다.
                    <br />
                  </HelpTooltip>
                </strong>
                {analysisPeriodConfig.map(config => {
                  const [value, text, active] = config
                  return (
                    <NoneAnchor
                      key={`$$${value}`}
                      data-tiara-action-name={`보고서_${getDomainText(
                        domain
                      )}_분석데이터_기간_${text}_클릭`}
                      className={classNames('link_option', {
                        on: analysisPeriod === value,
                        in_active: !active,
                      })}
                      onClick={() => dispatch(setAnalysisPeriod(value))}>
                      {text}
                    </NoneAnchor>
                  )
                })}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
export default AnalysisFilter
